import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Pencil } from "lucide-react";
import { Button } from "../common/Button";
import { Modal } from "../common/Modal";
import { TeamSection } from "./TeamSection";
import { useProjectTeam } from "./useProjectTeam";
import type { ProjectEditModalProps } from "./types";

export const ProjectEditModal = ({
  projectId,
  title,
  description,
  canManageTeam = false,
  onSave,
  onClose,
}: ProjectEditModalProps) => {
  const { t } = useTranslation();
  const [editTitle, setEditTitle] = useState(title || "");
  const [editDescription, setEditDescription] = useState(description || "");
  const [saving, setSaving] = useState(false);

  const team = useProjectTeam({ projectId, canManageTeam });

  const handleSave = async () => {
    if (!editTitle.trim()) return;

    setSaving(true);
    try {
      const teamChanges = canManageTeam ? team.getTeamChanges() : null;
      await onSave({
        name: editTitle.trim(),
        description: editDescription.trim(),
        teamChanges: teamChanges ?? undefined,
      });
      onClose();
    } catch (error) {
      // The caller already reports to Bugsnag and rethrows; surface it to the user.
      toast.error(t("project_edit.team_update_failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      className="max-h-[90vh] overflow-y-auto"
      disableBackdropClose={saving}
      disableEscapeClose={saving}
    >
      <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mx-auto">
        <Pencil size={22} className="text-blue-600" />
      </div>
      <h3 className="mt-4 text-lg font-semibold text-gray-900 text-center">
        {t("project_edit.edit_project")}
      </h3>
      <div className="mt-4 space-y-4">
        <div>
          <label
            htmlFor="modalEditTitle"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {t("project_edit.project_title")}{" "}
            <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="modalEditTitle"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder={t("project_edit.enter_title")}
          />
        </div>
        <div>
          <label
            htmlFor="modalEditDescription"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {t("project_edit.description")}{" "}
            <span className="text-gray-400 text-xs">
              ({t("project_edit.optional")})
            </span>
          </label>
          <textarea
            id="modalEditDescription"
            rows={3}
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder={t("project_edit.enter_description")}
          />
        </div>
        {canManageTeam && <TeamSection team={team} />}
      </div>
      <div className="mt-6 flex gap-3">
        <Button
          variant="default"
          size="lg"
          className="flex-1"
          onClick={onClose}
          disabled={saving}
        >
          {t("project_edit.cancel")}
        </Button>
        <Button
          variant="primary"
          size="lg"
          className="flex-1"
          onClick={handleSave}
          disabled={saving || !editTitle.trim()}
          startAdornment={
            saving && (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            )
          }
        >
          <span>
            {saving ? t("project_edit.saving") : t("project_edit.save")}
          </span>
        </Button>
      </div>
    </Modal>
  );
};
